import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MessagesSquare, Trash2, Loader2, ShieldCheck, ExternalLink, Search, User } from "lucide-react";

// Admin review of student questions asked via the VFRG reader's Ask-AI, with cited sources.
export default function StudentQuestions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get("/theory/ask-log"); setItems(r.data || []); }
    catch { toast.error("Failed to load student questions"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (it) => {
    if (!window.confirm("Delete this logged question?")) return;
    try { await api.delete(`/theory/ask-log/${it.id}`); setItems((xs) => xs.filter((x) => x.id !== it.id)); }
    catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((it) =>
      [it.question, it.answer, it.chapter_title, it.lesson_title, it.user_name].some((f) => (f || "").toLowerCase().includes(t)));
  }, [items, q]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-lime-500" /></div>;

  return (
    <div className="space-y-4" data-testid="student-questions">
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
        <MessagesSquare className="h-5 w-5 text-lime-500 shrink-0" />
        <p className="text-sm text-slate-600 flex-1">Questions pilots asked the AI while reading the VFRG, with the authoritative sources it cited. Great for spotting knowledge gaps and improving your training content.</p>
        <Badge variant="outline" className="text-slate-500 shrink-0">{items.length} total</Badge>
      </div>

      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions, answers, chapters or students…" className="pl-9" data-testid="sq-search" />
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-16" data-testid="sq-empty">
          {items.length === 0 ? "No questions yet. When a pilot uses “Ask AI about this topic” in a VFRG chapter, it appears here." : "No questions match your search."}
        </p>
      ) : (
        filtered.map((it) => (
          <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`sq-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{it.question}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {it.user_name || "Unknown"}</span>
                  {it.chapter_title && <span>· {it.chapter_title}</span>}
                  {it.lesson_title && <span>· {it.lesson_title}</span>}
                  {it.created_at && <span>· {new Date(it.created_at).toLocaleString()}</span>}
                </p>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600 shrink-0" onClick={() => remove(it)} data-testid={`sq-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>

            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid={`sq-answer-${it.id}`}>{it.answer}</div>

            {(it.sources || []).length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1.5"><ShieldCheck className="h-3.5 w-3.5 text-lime-600" /> Sources cited</p>
                <ul className="space-y-1">
                  {it.sources.map((s, i) => (
                    <li key={i} className="text-sm" data-testid={`sq-source-${it.id}-${i}`}>
                      <span className="font-medium text-slate-800">{s.name}</span>
                      {s.reference && <span className="text-slate-500"> — {s.reference}</span>}
                      {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 ml-1.5 text-sky-700 hover:underline"><ExternalLink className="h-3 w-3" /> open</a>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
